import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchSubjectById2,
  fetchModulesBySubject, createModuleForSubject, updateModuleById, deleteModuleById,
  fetchTopicsByModule, createTopicForModule, updateTopicById, deleteTopicById,
} from "../../pages/services/notesService";
import ConfirmationDialog from "../ReusableComponent/ConfirmationDialog";
import "../../styles/CourseNotes.css";
import "../../styles/ListPage.css";

const SubjectDetailsPage = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();

  const [subject, setSubject] = useState(null);
  const [modules, setModules] = useState([]);
  const [loadingSubject, setLoadingSubject] = useState(true);
  const [loadingModules, setLoadingModules] = useState(true);

  // expanded state: { [moduleId]: bool }
  const [expanded, setExpanded] = useState({});
  // topics per module: { [moduleId]: [] }
  const [topicsMap, setTopicsMap] = useState({});
  const [loadingTopics, setLoadingTopics] = useState({});

  // Module add/edit
  const [addingModule, setAddingModule] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [editingModuleId, setEditingModuleId] = useState(null);
  const [editingModuleTitle, setEditingModuleTitle] = useState("");

  // Topic add/edit
  const [addingTopicForModule, setAddingTopicForModule] = useState(null);
  const [newTopicTitle, setNewTopicTitle] = useState("");
  const [newTopicDuration, setNewTopicDuration] = useState("");
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editingTopicTitle, setEditingTopicTitle] = useState("");
  const [editingTopicDuration, setEditingTopicDuration] = useState("");

  // Delete confirm
  const [deleteDialog, setDeleteDialog] = useState({ show: false, message: "", onConfirm: null });

  const [toast, setToast] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };
  const confirmDelete = (message, onConfirm) => setDeleteDialog({ show: true, message, onConfirm });
  const closeDialog = () => setDeleteDialog({ show: false, message: "", onConfirm: null });

  useEffect(() => {
    fetchSubjectById2(subjectId)
      .then(setSubject)
      .catch(() => showToast("Failed to load subject"))
      .finally(() => setLoadingSubject(false));
    loadModules();
  }, [subjectId]);

  const loadModules = useCallback(async () => {
    setLoadingModules(true);
    try { setModules(await fetchModulesBySubject(subjectId)); } catch { setModules([]); }
    setLoadingModules(false);
  }, [subjectId]);

  const loadTopicsForModule = useCallback(async (moduleId) => {
    setLoadingTopics((prev) => ({ ...prev, [moduleId]: true }));
    try {
      const topics = await fetchTopicsByModule(moduleId);
      setTopicsMap((prev) => ({ ...prev, [moduleId]: topics }));
    } catch {
      setTopicsMap((prev) => ({ ...prev, [moduleId]: [] }));
    }
    setLoadingTopics((prev) => ({ ...prev, [moduleId]: false }));
  }, []);

  const toggleModule = (moduleId) => {
    const willExpand = !expanded[moduleId];
    setExpanded((prev) => ({ ...prev, [moduleId]: willExpand }));
    if (willExpand && !topicsMap[moduleId]) loadTopicsForModule(moduleId);
    // Close any open add/edit forms when collapsing
    if (!willExpand) {
      if (addingTopicForModule === moduleId) setAddingTopicForModule(null);
    }
  };

  // ── Module CRUD ──────────────────────────────────────────────────────────────

  const handleAddModule = async () => {
    if (!newModuleTitle.trim()) return;
    try {
      await createModuleForSubject(subjectId, newModuleTitle.trim());
      setNewModuleTitle(""); setAddingModule(false);
      await loadModules();
      showToast("Module added");
    } catch { showToast("Error adding module"); }
  };

  const handleUpdateModule = async (moduleId) => {
    if (!editingModuleTitle.trim()) return;
    try {
      await updateModuleById(subjectId, moduleId, editingModuleTitle.trim());
      setEditingModuleId(null);
      await loadModules();
      showToast("Module updated");
    } catch { showToast("Error updating module"); }
  };

  const handleDeleteModule = (moduleId, moduleTitle) => {
    confirmDelete(
      `Delete module "${moduleTitle}"? All topics inside will also be removed.`,
      async () => {
        closeDialog();
        try {
          await deleteModuleById(subjectId, moduleId);
          setExpanded((p) => { const n = { ...p }; delete n[moduleId]; return n; });
          setTopicsMap((p) => { const n = { ...p }; delete n[moduleId]; return n; });
          await loadModules();
          showToast("Module deleted");
        } catch { showToast("Error deleting module"); }
      }
    );
  };

  // ── Topic CRUD ───────────────────────────────────────────────────────────────

  const handleAddTopic = async (moduleId) => {
    if (!newTopicTitle.trim()) return;
    try {
      await createTopicForModule(moduleId, newTopicTitle.trim(), newTopicDuration);
      setNewTopicTitle(""); setNewTopicDuration(""); setAddingTopicForModule(null);
      await loadTopicsForModule(moduleId);
      showToast("Topic added");
    } catch { showToast("Error adding topic"); }
  };

  const handleUpdateTopic = async (moduleId, topicId) => {
    if (!editingTopicTitle.trim()) return;
    try {
      await updateTopicById(moduleId, topicId, editingTopicTitle.trim(), editingTopicDuration);
      setEditingTopicId(null);
      await loadTopicsForModule(moduleId);
      showToast("Topic updated");
    } catch { showToast("Error updating topic"); }
  };

  const handleDeleteTopic = (moduleId, topicId, topicTitle) => {
    confirmDelete(
      `Delete topic "${topicTitle}"?`,
      async () => {
        closeDialog();
        try {
          await deleteTopicById(moduleId, topicId);
          await loadTopicsForModule(moduleId);
          showToast("Topic deleted");
        } catch { showToast("Error deleting topic"); }
      }
    );
  };

  if (loadingSubject) {
    return <div className="cn-page"><div className="cn-loading" style={{ paddingTop: 60 }}>Loading...</div></div>;
  }

  return (
    <div className="cn-page">
      {toast && <div className="cn-toast">{toast}</div>}

      <ConfirmationDialog
        show={deleteDialog.show}
        type="delete"
        message={deleteDialog.message}
        onConfirm={deleteDialog.onConfirm}
        onCancel={closeDialog}
      />

      {/* Breadcrumb */}
      <div className="cn-breadcrumb">
        <span className="cn-breadcrumb-link" onClick={() => navigate("/notes")}>Notes</span>
        <span className="cn-breadcrumb-sep"> › </span>
        <span className="cn-breadcrumb-current">Subject: {subject?.title || "…"}</span>
      </div>

      {/* Header */}
      <div className="cn-header">
        <div>
          <h2 className="cn-title">{subject?.title || "Subject Details"}</h2>
          {subject?._id && (
            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>
              Subject ID: <span
                title="Click to copy"
                style={{ cursor: "pointer", userSelect: "all" }}
                onClick={() => navigator.clipboard?.writeText(subject._id)}
              >{subject._id}</span>
            </p>
          )}
        </div>
        <button className="cn-back-btn" onClick={() => navigate("/notes")}>← Back</button>
      </div>

      {/* Modules Section */}
      <div className="cn-sd-section">
        <div className="cn-sd-section-header">
          <span className="cn-sd-section-title">
            Modules <span className="cn-sd-count">({modules.length})</span>
          </span>
          <button className="cn-add-btn" onClick={() => { setAddingModule(true); setNewModuleTitle(""); }}>
            + Add Module
          </button>
        </div>

        {/* Add Module inline form */}
        {addingModule && (
          <div className="cn-sd-inline-form">
            <input
              className="cn-form-input"
              autoFocus
              placeholder="Module title..."
              value={newModuleTitle}
              onChange={(e) => setNewModuleTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleAddModule(); if (e.key === "Escape") setAddingModule(false); }}
            />
            <div className="cn-inline-actions" style={{ marginTop: 8 }}>
              <button className="cn-save-btn" onClick={handleAddModule}>Save</button>
              <button className="cn-cancel-btn" onClick={() => setAddingModule(false)}>Cancel</button>
            </div>
          </div>
        )}

        {loadingModules && <div className="cn-loading">Loading modules...</div>}
        {!loadingModules && modules.length === 0 && !addingModule && (
          <p className="cn-empty">No modules yet. Add one above.</p>
        )}

        {/* Module List */}
        <div className="cn-module-list">
          {modules.map((mod) => (
            <div key={mod._id} className="cn-module-card">

              {/* Module Row */}
              <div
                className="cn-module-row"
                onClick={() => { if (editingModuleId !== mod._id) toggleModule(mod._id); }}
              >
                <span className="cn-expand-icon">{expanded[mod._id] ? "▼" : "▶"}</span>

                {editingModuleId === mod._id ? (
                  <div className="cn-module-edit-wrap" onClick={(e) => e.stopPropagation()}>
                    <input
                      className="cn-inline-input"
                      autoFocus
                      value={editingModuleTitle}
                      onChange={(e) => setEditingModuleTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleUpdateModule(mod._id);
                        if (e.key === "Escape") setEditingModuleId(null);
                      }}
                    />
                    <button className="cn-save-btn" onClick={() => handleUpdateModule(mod._id)}>Save</button>
                    <button className="cn-cancel-btn" onClick={() => setEditingModuleId(null)}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span className="cn-module-title">{mod.title}</span>
                    <span
                      title="Module ID — click to copy"
                      style={{ fontSize: 10, color: "#9ca3af", fontFamily: "monospace", cursor: "pointer", userSelect: "all" }}
                      onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(mod._id); }}
                    >
                      ID: {mod._id}
                    </span>
                  </div>
                )}

                <div className="cn-module-actions" onClick={(e) => e.stopPropagation()}>
                  <span className="cn-topic-pill">
                    {topicsMap[mod._id] ? topicsMap[mod._id].length : "—"} topics
                  </span>
                  <button
                    className="cn-edit-btn"
                    onClick={() => { setEditingModuleId(mod._id); setEditingModuleTitle(mod.title); }}
                  >
                    Edit
                  </button>
                  <button className="cn-del-btn" onClick={() => handleDeleteModule(mod._id, mod.title)}>
                    Delete
                  </button>
                </div>
              </div>

              {/* Expanded Topics Panel */}
              {expanded[mod._id] && (
                <div className="cn-topics-panel">
                  <div className="cn-topics-panel-header">
                    <span className="cn-topics-panel-label">Topics</span>
                    <button
                      className="cn-add-btn"
                      style={{ fontSize: 11, padding: "4px 10px" }}
                      onClick={() => { setAddingTopicForModule(mod._id); setNewTopicTitle(""); }}
                    >
                      + Add Topic
                    </button>
                  </div>

                  {addingTopicForModule === mod._id && (
                    <div className="cn-sd-inline-form cn-topics-add-form">
                      <input
                        className="cn-form-input"
                        autoFocus
                        placeholder="Topic title..."
                        value={newTopicTitle}
                        onChange={(e) => setNewTopicTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleAddTopic(mod._id);
                          if (e.key === "Escape") setAddingTopicForModule(null);
                        }}
                      />
                      <input
                        className="cn-form-input"
                        type="number"
                        min="0"
                        placeholder="Reading duration (minutes, 0 = no lock)"
                        value={newTopicDuration}
                        onChange={(e) => setNewTopicDuration(e.target.value)}
                        style={{ marginTop: 6 }}
                      />
                      <div className="cn-inline-actions" style={{ marginTop: 6 }}>
                        <button className="cn-save-btn" onClick={() => handleAddTopic(mod._id)}>Save</button>
                        <button className="cn-cancel-btn" onClick={() => setAddingTopicForModule(null)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {loadingTopics[mod._id] && <div className="cn-loading">Loading topics...</div>}

                  {!loadingTopics[mod._id] && (topicsMap[mod._id] || []).length === 0 && addingTopicForModule !== mod._id && (
                    <p className="cn-empty" style={{ padding: "12px 16px" }}>No topics yet.</p>
                  )}

                  {(topicsMap[mod._id] || []).map((topic) => (
                    <div key={topic._id} className="cn-topic-row">
                      {editingTopicId === topic._id ? (
                        <div className="cn-module-edit-wrap" onClick={(e) => e.stopPropagation()} style={{ flex: 1 }}>
                          <input
                            className="cn-inline-input"
                            autoFocus
                            value={editingTopicTitle}
                            onChange={(e) => setEditingTopicTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleUpdateTopic(mod._id, topic._id);
                              if (e.key === "Escape") setEditingTopicId(null);
                            }}
                          />
                          <input
                            className="cn-inline-input"
                            type="number"
                            min="0"
                            placeholder="Reading duration (min)"
                            value={editingTopicDuration}
                            onChange={(e) => setEditingTopicDuration(e.target.value)}
                            style={{ marginTop: 4, width: 180 }}
                          />
                          <button className="cn-save-btn" onClick={() => handleUpdateTopic(mod._id, topic._id)}>Save</button>
                          <button className="cn-cancel-btn" onClick={() => setEditingTopicId(null)}>Cancel</button>
                        </div>
                      ) : (
                        <>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <span
                              className="cn-topic-title-link"
                              onClick={() => navigate(`/notes/subjects/${subjectId}/topics/${topic._id}`)}
                            >
                              {topic.title}
                            </span>
                            <span
                              title="Topic ID — click to copy"
                              style={{ fontSize: 10, color: "#9ca3af", fontFamily: "monospace", cursor: "pointer", userSelect: "all" }}
                              onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(topic._id); }}
                            >
                              ID: {topic._id}
                            </span>
                          </div>
                          <div className="cn-topic-actions">
                            <button
                              className="cn-view-btn"
                              onClick={() => navigate(`/notes/subjects/${subjectId}/topics/${topic._id}`)}
                            >
                              View →
                            </button>
                            <button
                              className="cn-edit-btn"
                              onClick={() => { setEditingTopicId(topic._id); setEditingTopicTitle(topic.title); setEditingTopicDuration(topic.readingDuration ?? ""); }}
                            >
                              Edit
                            </button>
                            <button
                              className="cn-del-btn"
                              onClick={() => handleDeleteTopic(mod._id, topic._id, topic.title)}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SubjectDetailsPage;
